import React, { useEffect } from "react";
import { Field, initialize, getFormValues } from "redux-form";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "reducers/Modal";
import { loadSharableLinks } from "reducers/CreativePreviews";

import {
	Button,
	ProgressIndicator,
	ReduxControls
} from "rhombus-react";

import { useModalStack } from "utils/common";
import BasicModal from "widgets/modals/BasicModal";

import LinkTable from "widgets/modals/creatives/preview/sharing/LinkTable";
import CopyLinksButton from "widgets/modals/creatives/preview/sharing/CopyLinksButton";
import { SHARE_CREATIVE_PREVIEWS_FORM } from "widgets/modals/creatives/preview/sharing/utils";

export function ViewLinksModal(props){
	const { useVersionLinks } = props;
	const title = useVersionLinks ? "Share Versions" : "Share Creatives";
	const dispatch = useDispatch();

	const {
		sharableLinks = [],
		isLoadingSharableLinks,
	} = useSelector(state => state.creativePreviews);

	function onClose(){
		dispatch(closeModal());
	}

	const modalProps = {
		title,
		className: "share-creative-previews-modal basic-modal",
		renderContent(){
			return (
				<div className="progress-container">
					<ProgressIndicator isLoading={isLoadingSharableLinks} />
					<p>These public preview links are now available and can be viewed by anyone you share the URL with:</p>
					<LinkTable
						links={sharableLinks}
						useVersionLinks={useVersionLinks}
					/>
				</div>
			);
		},
		renderFooter(){
			return (
				<>
					<Button
						theme = "default"
						onClick = {onClose}
					>
						Close
					</Button>
					<CopyLinksButton useVersionLinks={useVersionLinks} />
				</>
			);
		},
		onClose,
	};

	return <BasicModal {...modalProps} />;
}

ViewLinksModal.displayName = "ViewLinksModal";
ViewLinksModal.defaultProps = {
	useVersionLinks: false,
};

export default function ShareMultipleModal(props){
	const {
		creativeIds,
		useVersionLinks,
	} = props;

	const title = useVersionLinks ? "Share Versions" : "Share Creatives";
	const {
		isLoadingSharableLinks,
	} = useSelector(state => state.creativePreviews);
	const { id: libraryId } = useSelector(state => state.libraryProperties || {});
	const values = useSelector(getFormValues(SHARE_CREATIVE_PREVIEWS_FORM));

	const { name, isPermanent } = values || {};

	const [, { pushModal }, ModalStackHead] = useModalStack();

	const dispatch = useDispatch();

	function getLinks(){
		dispatch(loadSharableLinks(libraryId, creativeIds, name, isPermanent));
		pushModal(ViewLinksModal);
	}

	function onClose(){
		dispatch(closeModal());
	}

	useEffect(() => {
		const date = moment().format("YYYYMMDD");
		dispatch(initialize(SHARE_CREATIVE_PREVIEWS_FORM, {
			name: `${creativeIds.length} Creative ${date}`,
			isPermanent: false,
		}));
	}, []);

	const modalProps = {
		title,
		className: "share-creative-previews-modal basic-modal",
		renderContent(){
			return (
				<div className="progress-container">
					<ProgressIndicator isLoading={isLoadingSharableLinks} />
					<div className="fields">
						<Field
							name = "isPermanent"
							label = "Make links permanent (regular links expire after 90 days)"
							component = {ReduxControls.Checkbox}
							useValue
						/>
						<Field
							name = "name"
							label = "Preview Name"
							component = {ReduxControls.Input}
						/>
					</div>
				</div>
			);
		},
		renderFooter(){
			return (
				<>
					<Button
						theme = "default"
						onClick = {onClose}
					>
						Close
					</Button>

					<Button
						onClick = {getLinks}
						disabled = {isLoadingSharableLinks}
					>
						Get Links
					</Button>
				</>
			);
		},
		onClose,
	};

	return ModalStackHead ? <ModalStackHead useVersionLinks={useVersionLinks} /> : <BasicModal {...modalProps} />;
}

ShareMultipleModal.displayName = "ShareMultipleModal";
ShareMultipleModal.defaultProps = {
	creativeIds: [],
	useVersionLinks: false,
};
